import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CabinetHeader } from "../components/CabinetHeader";
import { Button } from "../components/Button";
import { ErrorBanner } from "../components/ErrorBanner";
import { getCabinetMe, getCabinetYears } from "../api/cabinet";
import { listPosts } from "../api/posts";
import { api } from "../api/client";
import { Post } from "../types/gordost";

export function PortfolioArchivePage() {
  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ["cabinet-me"],
    queryFn: getCabinetMe,
  });

  const [selectedChildId, setSelectedChildId] = useState<number | null>(null);
  const childId = selectedChildId ?? me?.children[0]?.id ?? null;

  const { data: postsData, isLoading: postsLoading } = useQuery({
    queryKey: ["posts-all", childId],
    queryFn: () =>
      childId
        ? listPosts({ student_id: childId, include_archived: true })
        : Promise.resolve({ items: [], total: 0 }),
    enabled: !!childId,
  });

  // Справочник годов id→{name,is_active} — чтобы показывать «2024/2025»,
  // а не «Год #5» для прошлых лет.
  const { data: years } = useQuery({
    queryKey: ["cabinet-years"],
    queryFn: getCabinetYears,
  });
  const yearMap = new Map((years ?? []).map((y) => [y.id, y]));

  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  async function downloadPdf() {
    if (!childId) return;
    setError("");
    setDownloading(true);
    try {
      const response = await api.get(`/portfolio/${childId}/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const studentName =
        me?.children.find((c) => c.id === childId)?.full_name ?? "portfolio";
      const safeName = studentName.replace(/[\\/:*?"<>|]/g, "_");
      link.download = `Гордость94_${safeName}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string | unknown } } };
      const msg = e?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Не удалось скачать PDF");
    } finally {
      setDownloading(false);
    }
  }

  if (meLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-paper)] flex items-center justify-center">
        <p className="text-[var(--color-muted)]">Загрузка…</p>
      </div>
    );
  }
  if (!me) {
    return (
      <div className="min-h-screen bg-[var(--color-paper)]">
        <CabinetHeader />
        <main className="max-w-3xl mx-auto px-6 py-8 text-center">
          <p className="text-[var(--color-accent)]">Ошибка загрузки</p>
        </main>
      </div>
    );
  }

  // Group posts by year_id
  const byYear = new Map<number, Post[]>();
  for (const p of postsData?.items ?? []) {
    const arr = byYear.get(p.year_id) ?? [];
    arr.push(p);
    byYear.set(p.year_id, arr);
  }
  const sortedYears = Array.from(byYear.keys()).sort((a, b) => b - a);

  return (
    <div className="min-h-screen bg-[var(--color-paper)]">
      <CabinetHeader displayName={me.parent.display_name} />
      <main className="max-w-3xl mx-auto px-6 py-8">
        <Link
          to="/cabinet"
          className="text-sm text-[var(--color-muted)] hover:text-[var(--color-accent)] mb-2 inline-block"
        >
          ← В кабинет
        </Link>
        <h1 className="text-3xl mb-2">Архив портфолио</h1>
        <p className="text-sm text-[var(--color-muted)] mb-6">
          Все одобренные публикации за все учебные годы. Можно скачать в PDF.
        </p>

        <ErrorBanner message={error} />

        {/* Child selector */}
        {me.children.length > 1 && (
          <div className="mb-6 flex flex-wrap gap-2 items-center">
            <span className="text-sm text-[var(--color-muted)]">Ребёнок:</span>
            {me.children.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedChildId(c.id)}
                className={`px-4 py-2 border-2 text-sm ${
                  childId === c.id
                    ? "bg-[var(--color-accent)] text-white border-[var(--color-accent)]"
                    : "bg-white border-[var(--color-line)] hover:border-[var(--color-accent)]"
                }`}
              >
                {c.full_name}
              </button>
            ))}
          </div>
        )}

        {childId && (
          <div className="mb-6">
            <Button
              variant="secondary"
              onClick={downloadPdf}
              loading={downloading}
            >
              📥 Скачать PDF портфолио
            </Button>
          </div>
        )}

        {postsLoading ? (
          <p className="text-[var(--color-muted)]">Загрузка публикаций…</p>
        ) : sortedYears.length === 0 ? (
          <p className="text-[var(--color-muted)] italic">
            Пока нет опубликованных работ.
          </p>
        ) : (
          sortedYears.map((yearId) => {
            const posts = byYear.get(yearId)!;
            const y = yearMap.get(yearId);
            const isCurrent =
              (me.current_year && me.current_year.id === yearId) ||
              y?.is_active;
            const baseName =
              y?.name ??
              (me.current_year && me.current_year.id === yearId
                ? me.current_year.name
                : `Год #${yearId}`);
            const yearName = `${baseName}${isCurrent ? " · текущий" : ""}`;
            return (
              <section key={yearId} className="mb-8">
                <h2
                  className="text-2xl mb-4 pb-2 border-b border-[var(--color-line)]"
                  style={{ fontFamily: "var(--font-serif)" }}
                >
                  {yearName} · {posts.length}{" "}
                  {posts.length === 1 ? "запись" : "записей"}
                </h2>
                <div className="space-y-3">
                  {posts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/cabinet/post/${post.id}`}
                      className="block bg-white border border-[var(--color-line)] p-4 hover:border-[var(--color-accent)] transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-bold">{post.title}</div>
                          <div className="text-sm text-[var(--color-muted)] mt-1">
                            {post.event_date &&
                              new Date(post.event_date).toLocaleDateString(
                                "ru-RU"
                              )}
                            {post.files.length > 0 &&
                              ` · ${post.files.length} ф.`}
                          </div>
                        </div>
                        {post.moderation_status === "approved" && (
                          <span className="px-2 py-1 text-xs font-bold bg-green-100 text-green-800 whitespace-nowrap">
                            ✓
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            );
          })
        )}
      </main>
    </div>
  );
}
